import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ОГОНЬКИ ФЕЙ И СВЕТ ВОЛШЕБНОГО ЛЕСА — эффект подложки magical_forest_2.
 *
 * 1. ОГОНЕК ФЕИ. Из-за деревьев на поляну вылетает огонек: мерцающая звездочка
 *    с цветным шлейфом и следом тающей пыльцы. Он летит не по прямой, а петлями —
 *    как росчерк пера, — и, не долетев до другой кромки, плавно гаснет над
 *    поляной. Иногда огоньков два: второй гонится за первым по тем же петлям (см.
 *    utils/effects/biome/fairyLights).
 *
 *    Так решил владелец игры, посмотрев эффект в игре: огонек — в полтора-два
 *    раза мельче прежнего, живет меньше и не пропадает, а затухает; красок не
 *    одна желто-белая, а те, которыми светится сам лес на картинке, — зеленая,
 *    голубая, сиреневая, фиолетовая; и за огоньком остается след пыльцы.
 *
 * 2. СВЕТ ЛЕСА. На картинке пейзажа нарисовано то, что светится само: голубые
 *    кристаллы и заводи, сиреневые и фиолетовые кроны, золотистые россыпи, а на
 *    поляне — светлые пятна мха, дымка и мелкие цветы. На каждом таком месте
 *    медленно дышит мягкое пятно его же цвета, а время от времени место
 *    «просыпается» — ненадолго разгорается чуть ярче.
 *
 * Огонек с шлейфом легко принять за игровое событие (заклинание, снаряд, искру
 * попадания). Поэтому он летит медленно и только петлями, а полет один на все
 * поле — с паузой до следующего.
 */
export const FAIRY_LIGHTS_KIND = 'fairyLights';

/**
 * Дымка на поляне своей насыщенной краски не имеет: ее проба — серо-голубая
 * (0x4b5c6b), а сиреневой она выглядит рядом с зеленым мхом. Свет дымке дан
 * сиреневый — тот же, что у сиреневой россыпи в лесу (0.191, 0.467; проба
 * 0x645b8f, в полную яркость 0xb2a3ff)
 */
const MIST_LILAC = 0xb2a3ff;

/**
 * Нарисованное светящееся место: середина и радиус (доли стороны картинки
 * пейзажа) и его краска. Места найдены на картинке и сверены вырезками с
 * наложенным кружком: каждое сидит на нарисованном огне. Краска — проба с
 * картинки внутри кружка: среднее по самой светящейся пятой части его точек,
 * приведенное к полной яркости (оттенок и насыщенность — как нарисовано; сама
 * проба — в примечании к месту). Сменится картинка — места сверить заново.
 */
const glowSpot = (u, v, radius, tint) => Object.freeze({u, v, radius, tint});

export const FAIRY_LIGHTS_EFFECT = Object.freeze({
  kind: FAIRY_LIGHTS_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.MAGICAL_FOREST_2]: Object.freeze({
      // Первый полет — вскоре после начала
      firstDelayMs: {min: 4000, max: 10000},
      // Пауза между полетами: поляна пуста от мига, когда огонек догорел, до
      // вылета следующего. Двух полетов разом не бывает. Пауза прежняя, а
      // огонек живет меньше — вылетают они теперь чаще (в среднем раз в 24
      // секунды, а не в 33), и над поляной огонек половину времени (прежде —
      // две трети)
      pauseMs: {min: 6000, max: 14000},
      // Какая доля полетов — пара: второй огонек гонится за первым
      pairShare: 0.35,
      // Поляна на картинке пейзажа (доли ее стороны): почти круг, кромка —
      // линия деревьев. Огоньки вылетают с этой линии
      glade: {u: 0.5, v: 0.52, radiusU: 0.235},
      // Проступает огонек чуть в глубине леса, над кронами: на таком расстоянии
      // от середины поляны, в долях ее радиуса. Ровно на кромке он возникал бы
      // уже над травой — кромка у поляны неровная
      entryShare: 1.06,
      // Полет проходит через точку, на которую смотрит камера; точка — не
      // дальше этой доли радиуса поляны от ее середины. Иначе путь вышел бы
      // короткой хордой по самой кромке леса
      throughShare: 0.55,
      // Путь через поляну — пологая дуга: прогиб в долях длины хорды
      bowShare: {min: 0.08, max: 0.2},
      // СРОК ЖИЗНИ. Огонек летит по дуге от кромки до кромки, но всю ее не
      // пролетает: гаснет над поляной
      life: {
        // Какую долю перелета через поляну он живет. Прежде пролетал ее всю (на
        // поле в 217 ячеек — 14–31 секунду, в среднем 22); теперь — от
        // половины до двух третей: 7–21 секунда, в среднем около 13. Короче
        // половины полет читался бы вспышкой: огонек не успевал бы отлететь
        // от деревьев
        share: {min: 0.5, max: 0.67},
        // Вылетает огонек с ближней к камере кромки и мимо точки, на которую
        // она смотрит, пролетает на этой доле своей жизни — еще не начав
        // гаснуть. Срок при этом остается в пределах выше: до самой середины
        // поляны огонек долетает, уже померкнув на треть
        passAtShare: 0.6,
        // Проступает над кронами за столько миллисекунд — мягко, без хлопка
        fadeInMs: 1200,
        // Гаснет долго: всю эту долю жизни под конец (3–8 секунд) свет и шлейф
        // плавно сходят на нет
        fadeOutShare: 0.4,
      },
      loop: {
        // Радиус петли, размеров ячейки (ячейка шириной в два размера): петля
        // около полутора ячеек в поперечнике
        radiusFactor: {min: 1.2, max: 1.6},
        // Одна петля — за столько миллисекунд: неспешно, глаз успевает проследить
        periodMs: {min: 2200, max: 2900},
        // Петля — овал: поперечник вдоль второй оси во столько раз меньше
        squash: 0.8,
        // Во сколько раз огонек бежит по петле быстрее, чем сама петля плывет
        // через поляну. Больше единицы — путь пересекает сам себя, то есть это
        // настоящие петли, а не волна; около трех — петли вплотную, как в
        // рукописной строчке
        speedRatio: {min: 2.3, max: 3},
        // Петли не одинаковые. Радиус дышит на такую долю с периодом во столько
        // петель (нецелое число — соседние петли разного размера)
        breatheDepth: 0.25,
        breathePeriods: {min: 1.55, max: 1.9},
        // И сама петля медленно ходит по встречному кругу: его радиус — в
        // долях радиуса петли, период — во столько петель. От этого петли то
        // теснятся, то расходятся и уходят от дуги то в одну, то в другую сторону
        swayShare: {min: 0.35, max: 0.6},
        swayPeriods: {min: 2.6, max: 3.4},
        // Огонек вылетает точно с линии деревьев и раскручивает петлю за
        // столько миллисекунд
        spiralMs: 1800,
      },
      // Второй огонек пары летит тем же путем, отставая на такую долю петли:
      // около половины — они кружат друг за другом
      pairLagShare: {min: 0.36, max: 0.5},
      /**
       * КРАСКИ ОГОНЬКОВ — те, которыми светится лес на картинке. У каждого
       * огонька краска одна: ею светятся ореол, шлейф и пыльца, а ядро и
       * звездочка — белые с ее примесью. В паре краски разные. weight — как
       * часто краска выпадает.
       *
       * Оттенок каждой — проба с картинки (место и проба — в примечании), а
       * насыщенность поднята: свет огонька складывается с цветом поляны, а она
       * под дымкой пустых ячеек средняя по яркости и зеленовато-голубая. Бледная
       * краска с картинки (она нарисована на темном лесу) на такой траве
       * выцветает в бело-голубую, и чем краснее краска, тем сильнее: поэтому
       * фиолетовая и сиреневая гуще прочих.
       */
      palette: Object.freeze([
        // Зеленая: светло-зеленый куст посреди поляны (0.507, 0.570), проба
        // 0x5c8c6c — оттенок 140°
        Object.freeze({id: 'green', tint: 0x5cff92, weight: 3}),
        // Голубая: кристаллы у северной кромки поляны (0.388, 0.286), проба
        // 0x247587 — оттенок 191°; это она же в полную яркость, без правок
        Object.freeze({id: 'cyan', tint: 0x45deff, weight: 3}),
        // Фиолетовая: нижний узел розово-сиреневой кроны у северо-западной
        // кромки (0.286, 0.345), проба 0x8270a8 — оттенок 259°
        Object.freeze({id: 'violet', tint: 0x8040ff, weight: 3}),
        // Сиреневая: самые светлые искры розовой россыпи на северо-востоке
        // (0.745, 0.354), проба 0xd3c2d9 — оттенок 283°
        Object.freeze({id: 'lilac', tint: 0xd66bff, weight: 3}),
        // Золотая — изредка: золотая россыпь на юго-востоке (0.747, 0.813),
        // проба 0xc1bcac — оттенок 45°. Золотистые кроны в лесу нарисованы, но
        // их меньше прочих огней — и золотой огонек выпадает один из тринадцати.
        // Прежде все огоньки были желто-белыми: владелец игры просил краски леса
        Object.freeze({id: 'gold', tint: 0xffd873, weight: 1}),
      ]),
      head: {
        // Звездочка: кадр текстуры, размеров ячейки; видимые лучи — около двух
        // третей кадра, то есть треть ячейки от кончика до кончика. Все части
        // огонька — в 1.7 раза мельче прежних (прежде: звездочка 1.9, ядро
        // 0.85, ореол 2.8)
        starSizeFactor: 1.1,
        // Звездочка и ядро — белые с примесью краски огонька: такая ее доля
        starTintShare: 0.2,
        starAlpha: 0.95,
        // Медленно поворачивается
        starSpinDegPerSecond: 40,
        // Мерцает: размер гуляет на такую долю столько раз в секунду
        twinkleDepth: 0.2,
        twinkleHz: 4,
        // Ядро: кадр мягкого пятна (видимое пятно — половина кадра). На
        // телефоне оно в пять точек экрана — мельче огонек терялся бы. Ядро
        // неплотное: у звездочки своя яркая середина, и вдвоем в полную силу
        // они выжигали бы добела отряд, над которым пролетает огонек
        coreSizeFactor: 0.5,
        coreTintShare: 0.35,
        coreAlpha: 0.45,
        // Цветной ореол вокруг головы
        haloSizeFactor: 1.65,
        haloAlpha: 0.3,
      },
      trail: {
        // Шлейф — копии головы с отставанием: столько пятен, каждое следующее
        // позже на столько миллисекунд. Шлейф повторяет изгиб петли. Он и
        // тоньше, и короче прежнего в те же 1.7 раза (прежде — 12 пятен через
        // 55 миллисекунд, 0.8 и 0.3 размера ячейки)
        count: 10,
        stepMs: 40,
        // Кадр пятна у головы и в хвосте, размеров ячейки
        sizeFactor: {head: 0.47, tail: 0.18},
        // На быстром участке петли пятна расходятся, и каждое вытягивается вдоль
        // пути: его кадр — не короче стольких расстояний до соседнего пятна.
        // Видимая часть мягкого пятна — треть кадра, так что при тройке пятна
        // смыкаются и шлейф остается сплошным, а не рассыпается на бусины
        stretch: 3,
        // Плотность у головы; к хвосту падает квадратично
        alpha: 0.85,
      },
      /**
       * ПЫЛЬЦА. Огонек роняет за собой мелкие искры своей краски: искра
       * рождается там, где огонек был в этот миг, остается на месте (чуть
       * расплывается и оседает), мерцает и за секунду тает. Это тонкий след за
       * шлейфом, а не хвост кометы: искр немного, и они мелкие.
       */
      dust: {
        // Искр у огонька разом не больше стольких (столько под них спрайтов)…
        count: 12,
        // …и роняет он их по одной через столько миллисекунд. Место искры
        // освобождается через count * shedEveryMs — не раньше, чем она дотает
        shedEveryMs: 110,
        // Искра живет столько — около секунды, каждая свое
        lifeMs: {min: 800, max: 1300},
        // Кадр мягкого пятна, размеров ячейки (видимое пятно — половина кадра):
        // вчетверо-вшестеро мельче ореола огонька
        sizeFactor: {min: 0.26, max: 0.42},
        // К концу жизни искра сжимается до этой доли размера
        endScale: 0.5,
        // Проступает за столько миллисекунд и тает как (1 - t)^fadePower
        alpha: 0.9,
        fadeInMs: 90,
        fadePower: 1.2,
        // Мерцает: яркость падает на такую долю столько раз в секунду
        twinkleDepth: 0.45,
        twinkleHz: 5,
        // За жизнь отплывает от места рождения не дальше стольких размеров
        // ячейки — в свою сторону — и оседает вниз по экрану на столько
        spreadFactor: 0.3,
        fallFactor: 0.18,
      },
      /**
       * СВЕТ ЛЕСА: мягкие пятна на нарисованных светящихся местах. Лежат на
       * самой земле — под дымкой пустых ячеек и под жетонами: это свет
       * картинки, а не подсветка ячейки. Сквозь дымку он виден и на поляне
       * (сверено на макетах поля) — поднимать его над дымкой не пришлось. Ветер
       * матча пятнам не нужен, и на старте партии они не перестраиваются.
       * Светятся все места и всегда: их меньше, чем эффекту можно держать
       * спрайтов, и выбирать среди них по камере незачем.
       */
      glows: {
        layer: 'GROUND_GLOW',
        // Кадр мягкого пятна шире нарисованного места во столько раз (яркая
        // середина пятна — около трети кадра, то есть с само место)
        sizeScale: 2.8,
        breath: {
          // Один вдох-выдох — за столько: у каждого места свой срок и своя
          // фаза, в такт места не дышат
          periodMs: {min: 4500, max: 8000},
          // Дыхание неровное: к основной волне примешана вторая, короче (такая
          // доля срока), в такой доле
          secondPeriodShare: 0.37,
          secondMix: 0.25,
          // Яркость на выдохе и на вдохе: совсем место не гаснет. Числа
          // подняты в два с лишним раза (было 0.1–0.26): владелец игры
          // подсветку в игре почти не замечал и попросил «значительно ярче»
          alpha: {min: 0.28, max: 0.6},
          // На выдохе пятно чуть меньше: эта доля размера
          swellFrom: 0.88,
        },
        wake: {
          // Место может проснуться раз в столько (у каждого свой срок) и
          // просыпается с такой вероятностью: в среднем раз в 40 секунд, так
          // что из 68 мест разом разгораются пять-шесть
          everyMs: {min: 12000, max: 22000},
          chance: 0.4,
          // Разгорается и гаснет одной плавной волной за столько
          durationMs: 3600,
          // На гребне к яркости дыхания прибавляется до стольких — всякий раз
          // по-своему, но не меньше этой доли; пятно чуть разбухает
          alpha: 0.3,
          minStrength: 0.5,
          swell: 0.12,
        },
        // Ярче этого пятну не бывать (дыхание и пробуждение вместе). Прежний
        // потолок 0.45 берег нарисованные искры от выгорания в белое, но при
        // нем свет леса в игре терялся — подняли вместе с дыханием
        maxAlpha: 0.9,
        spots: Object.freeze([
          // ЛЕС ВОКРУГ ПОЛЯНЫ, северо-запад
          glowSpot(0.3381, 0.2019, 0.014, 0x839dff), // большая сиреневая крона (проба 0x586aac)
          glowSpot(0.4550, 0.1970, 0.011, 0x7e9cff), // фиолетовая крона (проба 0x5164a4)
          glowSpot(0.4712, 0.1273, 0.014, 0x6fa0ff), // россыпь сиреневых искр (проба 0x405c94)
          glowSpot(0.3682, 0.1929, 0.008, 0x57e1ff), // голубые искры в кроне (проба 0x3fa3b8)
          glowSpot(0.4442, 0.1603, 0.010, 0x52d2ff), // полоса голубых кристаллов (проба 0x40a5c8)
          glowSpot(0.1716, 0.2719, 0.009, 0x59e0ff), // бирюзовая крона (проба 0x3e9cb2)
          glowSpot(0.2838, 0.3352, 0.012, 0x97a6ff), // розово-сиреневая крона у кромки поляны (проба 0x525a8a)
          glowSpot(0.1262, 0.4729, 0.010, 0x8c98ff), // фиолетовая россыпь (проба 0x4c538b)
          glowSpot(0.1908, 0.4672, 0.008, 0xb2a3ff), // сиреневая россыпь (проба 0x645b8f)
          glowSpot(0.1050, 0.3370, 0.008, 0xdcd1ff), // розовые искры (проба 0xbbb2d9)
          // Северо-восток
          glowSpot(0.6250, 0.2580, 0.012, 0x7f9aff), // фиолетовая крона (проба 0x3d4b7b)
          glowSpot(0.5795, 0.2651, 0.013, 0x8ea4ff), // фиолетовая крона (проба 0x45507c)
          glowSpot(0.5935, 0.2759, 0.006, 0x7bfffb), // мятный огонек (проба 0x50a5a2)
          glowSpot(0.6705, 0.3090, 0.014, 0x8e9cff), // большая фиолетовая крона (проба 0x4a5185)
          glowSpot(0.7454, 0.3537, 0.012, 0xe8dcff), // розовая россыпь (проба 0xc9bedc)
          glowSpot(0.5828, 0.2030, 0.012, 0x799aff), // фиолетовая крона (проба 0x455892)
          glowSpot(0.6673, 0.2276, 0.008, 0x5fdeff), // светящаяся вода (проба 0x4cb0cb)
          glowSpot(0.8157, 0.3421, 0.008, 0x5fdbff), // светящаяся заводь (проба 0x439ab3)
          glowSpot(0.8840, 0.3722, 0.010, 0x73a2ff), // фиолетовая россыпь (проба 0x3a5180)
          glowSpot(0.7670, 0.4549, 0.014, 0x98aaff), // фиолетовая крона (проба 0x535d8b)
          glowSpot(0.8576, 0.2503, 0.009, 0xffefe9), // золотистая россыпь (проба 0xb0a5a1)
          // Юго-запад
          glowSpot(0.2255, 0.7517, 0.014, 0x96b0ff), // большая фиолетовая крона (проба 0x5c6c9d)
          glowSpot(0.4224, 0.8170, 0.010, 0xbcaaff), // сиреневая крона (проба 0x685f8e)
          glowSpot(0.4338, 0.7765, 0.010, 0xbbadff), // сиреневая крона (проба 0x71699a)
          glowSpot(0.3935, 0.8778, 0.012, 0xb3a6ff), // сиреневая крона (проба 0x7a71ad)
          glowSpot(0.2704, 0.8890, 0.009, 0x98adff), // розово-сиреневая россыпь (проба 0x4e5882)
          glowSpot(0.3096, 0.6982, 0.014, 0xadbdff), // сиреневая крона в дымке у кромки поляны (проба 0x60698d)
          glowSpot(0.2025, 0.8466, 0.012, 0xfff5e1), // золотистая крона (проба 0xb5aea0)
          glowSpot(0.2000, 0.8050, 0.016, 0x7ce2ff), // бирюзовое озерцо (проба 0x4d8c9f)
          glowSpot(0.1356, 0.8670, 0.012, 0x8aeeff), // бирюзовое озерцо (проба 0x5ea2ae)
          // Юго-восток
          glowSpot(0.8813, 0.5834, 0.010, 0xa1a6ff), // фиолетовая крона (проба 0x5a5d8f)
          glowSpot(0.8153, 0.5944, 0.010, 0xa6a4ff), // сиреневая крона (проба 0x52517e)
          glowSpot(0.5411, 0.8441, 0.010, 0x92acff), // фиолетовая крона (проба 0x566596)
          glowSpot(0.7026, 0.8806, 0.016, 0x8ca0ff), // большая сине-фиолетовая крона (проба 0x546099)
          glowSpot(0.6500, 0.7220, 0.012, MIST_LILAC), // сиреневая дымка над кроной у кромки поляны
          glowSpot(0.8501, 0.5852, 0.008, 0x78f3ff), // голубая крона с серпом (проба 0x55acb5)
          glowSpot(0.8986, 0.6916, 0.014, 0x84deff), // светящаяся заводь (проба 0x5a98af)
          glowSpot(0.8167, 0.7969, 0.008, 0x83e8ff), // голубые искры в кроне (проба 0x589cab)
          glowSpot(0.8834, 0.7931, 0.008, 0x6af6ff), // голубые искры в кроне (проба 0x41979d)
          glowSpot(0.5137, 0.8656, 0.012, 0x61edff), // голубые искры в кроне (проба 0x42a0ac)
          glowSpot(0.7472, 0.8132, 0.008, 0xfff8e3), // золотая россыпь (проба 0xc1bcac)
          glowSpot(0.7301, 0.7564, 0.006, 0xd8fbff), // белые кристаллы (проба 0xc8e8ec)
          // ПОЛЯНА (под игровыми ячейками), с севера на юг
          glowSpot(0.3882, 0.2858, 0.010, 0x45deff), // голубые кристаллы у северной кромки (проба 0x247587)
          glowSpot(0.5150, 0.3300, 0.020, 0x94ffff), // светлое бирюзовое пятно мха (проба 0x5b9d9e)
          glowSpot(0.6350, 0.3120, 0.012, 0xb2ffd4), // зеленое свечение с бледным кольцом (проба 0x75a78a)
          glowSpot(0.3102, 0.4003, 0.007, 0xefffd4), // бледно-желтые цветы (проба 0x9ea98c)
          glowSpot(0.6100, 0.4050, 0.012, 0xaaffda), // мятное пятно (проба 0x6ea58d)
          glowSpot(0.6650, 0.4430, 0.012, MIST_LILAC), // фиолетовая дымка
          glowSpot(0.6940, 0.4960, 0.008, 0xe8f8ff), // розовые цветы (проба 0x919b9f)
          glowSpot(0.6423, 0.5201, 0.007, 0xd0fdff), // белые цветы (проба 0x90b0b1)
          glowSpot(0.3300, 0.5270, 0.012, MIST_LILAC), // сиреневая дымка
          glowSpot(0.3900, 0.5300, 0.018, 0x91fff9), // бирюзовое пятно мха (проба 0x53928f)
          glowSpot(0.2670, 0.5570, 0.006, 0x97d9ff), // голубой цветок у западной кромки (проба 0x6895af)
          glowSpot(0.5070, 0.5700, 0.012, 0xa8ffc4), // светло-зеленый куст (проба 0x5c8c6c)
          glowSpot(0.3454, 0.5706, 0.005, 0xb0eaff), // бледно-голубой цветок (проба 0x8ab8c8)
          glowSpot(0.3066, 0.5752, 0.008, 0x8de1ff), // голубые цветы (проба 0x598ea1)
          glowSpot(0.7177, 0.5911, 0.008, 0x8df2ff), // голубой куст (проба 0x518c94)
          glowSpot(0.5750, 0.5940, 0.009, 0xe4fff5), // бело-розовые цветы (проба 0x96a8a2)
          glowSpot(0.4464, 0.6007, 0.006, 0xcaf3ff), // бледно-голубые цветы (проба 0x8dabb3)
          glowSpot(0.3979, 0.6299, 0.010, 0xd1f3ff), // бело-голубые цветы (проба 0x97afb8)
          glowSpot(0.4250, 0.6500, 0.014, 0x89fff5), // бирюзовое пятно (проба 0x4a8a85)
          glowSpot(0.6520, 0.6600, 0.016, 0x88f4ff), // бирюзовое пятно (проба 0x467e83)
          glowSpot(0.6250, 0.6750, 0.014, MIST_LILAC), // фиолетовая дымка
          glowSpot(0.7020, 0.7000, 0.010, 0xcce4ff), // сиренево-розовые цветы (проба 0x96a8bb)
          glowSpot(0.5243, 0.7136, 0.005, 0x8be3ff), // голубые цветы (проба 0x67a8bc)
          glowSpot(0.4700, 0.7200, 0.010, 0xb0ffb5), // желто-зеленый куст (проба 0x5e8961)
          glowSpot(0.4222, 0.7358, 0.006, 0xbbf6ff), // бледно-голубой цветок (проба 0x94c2ca)
          glowSpot(0.5399, 0.7405, 0.008, 0x9ec7ff), // сине-фиолетовые цветы (проба 0x647ea1)
        ]),
      },
    }),
  }),
});
